// Column definitions for the Deposit Sheet admin tab (DepositSheet.jsx).
// Mirrors the column-list pattern used by `checkColumns.js`,
// `journalEntry.js` (journalEntryColumns) and `associateFee.js`
// (payrollColumns) so all per-tab column metadata lives under utils/.
//
// The deposit-sheet table is rendered inline in DepositSheet.jsx with
// custom cell coloring + match badges, so we only export plain data here
// (column list, color palette, and the per-card match-key map) — no
// JSX render functions.

/**
 * Columns shown in the Daily Collection Breakdown table. Order matters
 * for rendering and CSV export. Mirrors `MONTHLY_TABLE_COLUMNS` in
 * `pages/reporting/CollectionBreakdown.jsx`.
 */
export const DEPOSIT_SHEET_COLUMNS = [
  { key: "collection_cash", label: "Cash" },
  { key: "collection_debit", label: "Debit" },
  { key: "collection_mastercard", label: "Mastercard" },
  { key: "collection_visa", label: "Visa" },
  { key: "collection_amex", label: "Amex" },
  { key: "collection_checks", label: "Checks" },
  { key: "collection_insurance_check", label: "Insurance Check" },
  { key: "collection_personal_eft", label: "Personal EFT" },
  { key: "collection_insurance_eft", label: "Insurance EFT" },
  { key: "refund", label: "Refund" },
  { key: "collection_other", label: "Other" },
  // ── reconciliation trio (derived — see DEPOSIT_SHEET_DERIVED_KEYS) ──────────
  // `Total` used to sit here as the raw `collection_total`. It was replaced by
  // the figure that is actually comparable to NetSuite: cash is not part of an
  // electronic deposit, so a raw total differed from NetSuite by the day's cash
  // on every reconciled day.
  { key: "deposit_total", label: "Deposit Total", hint: "Total − Cash" },
  { key: "netsuite_total", label: "NetSuite Total", hint: "Sum of NetSuite deposit lines" },
  { key: "deposit_difference", label: "NetSuite Diff.", hint: "Deposit Total − NetSuite Total" },
  // Intercompany deposit lines ("Intercompany ART to SFC") are transfers between
  // our own subsidiaries, so they are kept out of NetSuite Total and shown here.
  { key: "intercompany", label: "Intercompany", hint: "Not in NetSuite Total" },
  // ── bank pair (derived): what Daily Deposits has Accepted against the bank ──
  { key: "bank_matched", label: "Bank Matched", hint: "Accepted on Daily Deposits" },
  { key: "bank_difference", label: "Bank Diff.", hint: "Bank-bound − Bank Matched" },
];

/**
 * Collection Breakdown fields that reach the bank, i.e. the ones Daily Deposits
 * turns into deposit lines to match ("<date>:<field>"). Cash and Other never
 * arrive at the bank; Refund is money out. Shared by Daily Deposits and the
 * Bank columns here, so both sides agree on what "bank-bound" means.
 */
export const DEPOSIT_SHEET_BANK_FIELDS = DEPOSIT_SHEET_COLUMNS.filter(
  (c) =>
    c.key.startsWith("collection_") && c.key !== "collection_cash" && c.key !== "collection_other",
);

/**
 * Columns whose value is COMPUTED, not read off the collection-breakdown record.
 *
 * `record[key]` is undefined for these, so the table, footer and CSV export all
 * route them through the page's own resolver instead.
 */
export const DEPOSIT_SHEET_DERIVED_KEYS = new Set([
  "deposit_total",
  "netsuite_total",
  "deposit_difference",
  "intercompany",
  "bank_matched",
  "bank_difference",
]);

/**
 * Record fields the monthly footer must sum even when no column displays them.
 * `collection_total` and `collection_cash` are the inputs to `deposit_total`, and
 * `refund` feeds the ± refund tolerance, so the footer needs their sums whether
 * or not they are on screen.
 */
export const DEPOSIT_SHEET_TOTAL_ONLY_KEYS = ["collection_total"];

/**
 * Color palette per column. References the shared `palette.<color>`
 * tokens defined in `tailwind.config.js`. Each entry picks a common
 * color family; re-theming a column means swapping the color name.
 *   - `badge` — solid background (row badges / chips)
 *   - `soft`  — tinted background + matching foreground (headers/footer)
 */
export const DEPOSIT_SHEET_COLUMN_COLORS = {
  collection_cash: {
    badge: "bg-palette-emerald-badge",
    soft: "bg-palette-emerald-soft text-palette-emerald-text",
  },
  collection_debit: {
    badge: "bg-palette-sky-badge",
    soft: "bg-palette-sky-soft text-palette-sky-text",
  },
  collection_mastercard: {
    badge: "bg-palette-sky-badge",
    soft: "bg-palette-sky-soft text-palette-sky-text",
  },
  collection_visa: {
    badge: "bg-palette-sky-badge",
    soft: "bg-palette-sky-soft text-palette-sky-text",
  },
  collection_amex: {
    badge: "bg-palette-sky-badge",
    soft: "bg-palette-sky-soft text-palette-sky-text",
  },
  collection_checks: {
    badge: "bg-palette-teal-badge",
    soft: "bg-palette-teal-soft text-palette-teal-text",
  },
  collection_insurance_check: {
    badge: "bg-palette-cyan-badge",
    soft: "bg-palette-cyan-soft text-palette-cyan-text",
  },
  collection_personal_eft: {
    badge: "bg-palette-violet-badge",
    soft: "bg-palette-violet-soft text-palette-violet-text",
  },
  collection_insurance_eft: {
    badge: "bg-palette-purple-badge",
    soft: "bg-palette-purple-soft text-palette-purple-text",
  },
  refund: { badge: "bg-palette-rose-badge", soft: "bg-palette-rose-soft text-palette-rose-text" },
  collection_other: {
    badge: "bg-palette-slate-badge",
    soft: "bg-palette-slate-soft text-palette-slate-text",
  },
  deposit_total: {
    badge: "bg-palette-gray-badge",
    soft: "bg-palette-gray-soft text-palette-gray-text",
  },
  netsuite_total: {
    badge: "bg-palette-indigo-badge",
    soft: "bg-palette-indigo-soft text-palette-indigo-text",
  },
  deposit_difference: {
    badge: "bg-palette-indigo-badge",
    soft: "bg-palette-indigo-soft text-palette-indigo-text",
  },
  // The bank pair shares one colour family, as the NetSuite pair shares indigo,
  // so the two reconciliations read as two groups.
  bank_matched: {
    badge: "bg-palette-teal-badge",
    soft: "bg-palette-teal-soft text-palette-teal-text",
  },
  bank_difference: {
    badge: "bg-palette-teal-badge",
    soft: "bg-palette-teal-soft text-palette-teal-text",
  },
  intercompany: {
    badge: "bg-palette-orange-badge",
    soft: "bg-palette-orange-soft text-palette-orange-text",
  },
};

/**
 * Card-type columns we attempt to match against NetSuite `transactionLine`
 * buckets (memo prefix VSA / MC / AMX / EF). Order matters — we count how
 * many of these align between a deposit-sheet row and a candidate
 * transaction.
 */
export const DEPOSIT_SHEET_MATCH_KEYS = [
  { col: "collection_debit", tx: "debit" },
  { col: "collection_mastercard", tx: "mastercard" },
  { col: "collection_visa", tx: "visa" },
  { col: "collection_amex", tx: "amex" },
];

/** Set of column keys that participate in NetSuite card-bucket matching. */
export const DEPOSIT_SHEET_MATCH_COL_KEYS = new Set(DEPOSIT_SHEET_MATCH_KEYS.map((m) => m.col));

// Collection Breakdown refund types (backend RefundType): which collection
// method a refund went back through. A record's `refunds` is [{type, amount}].
export const REFUND_TYPE_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "debit", label: "Debit" },
  { value: "mastercard", label: "Mastercard" },
  { value: "visa", label: "Visa" },
  { value: "amex", label: "Amex" },
  { value: "checks", label: "Checks" },
  { value: "insurance_check", label: "Insurance checks" },
  { value: "personal_eft", label: "Personal EFTs" },
  { value: "insurance_eft", label: "Insurance EFTs" },
];

export const refundTypeLabel = (type) =>
  REFUND_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? "";

/** Total refunded through one collection field, e.g. "collection_visa" (always ≥ 0). */
export const refundForField = (record, fieldKey) =>
  (record?.refunds ?? [])
    .filter((r) => r.type && fieldKey === `collection_${r.type}`)
    .reduce((s, r) => s + Math.abs(Number(r.amount) || 0), 0);

/** "Visa 10.00 · Amex 5.00" — a record's refunds by type; untyped ones are left out. */
export const refundsSummary = (record) =>
  (record?.refunds ?? [])
    .filter((r) => r.type)
    .map((r) => `${refundTypeLabel(r.type)} ${Number(r.amount).toFixed(2)}`)
    .join(" · ");
